// Talking NPCs in VR (hands first: everything is a tap or a pinch).
//
// Walk up to an NPC that talks and a "Talk to Zora" button floats between you
// and them. Tap it and a panel follows you at chest height: the NPC's latest
// line as a caption, a big mic button (tap to talk, tap again to send; it also
// sends on its own after 15 s) and a keyboard for typing. Same conversation,
// credits and voice as the desktop chat (useNpcConversation).
import React, { useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import type { PrimitiveItem } from '../../stores/worldStore';
import { vrColors as colors, noRaycast, Surface } from '../VR/vrUi';
import VRKeyboard, { KEYBOARD_HEIGHT, type KeyAction } from '../VR/VRKeyboard';
import { FloatingPanelMount } from '../games/kit/vrKit';
import { useNpcTalk, npcBodies } from './npcTalkStore';
import { useNpcConversation, useNpcCanTalk, type Line } from './useNpcConversation';

const _head = new THREE.Vector3();
const _npc = new THREE.Vector3();
const _p = new THREE.Vector3();

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function Btn({ label, x, y, w, h = .06, fontSize = .019, tone = 'card', disabled = false, name, onPress }: {
  label: string; x: number; y: number; w: number; h?: number; fontSize?: number; tone?: 'card' | 'teal' | 'red'; disabled?: boolean; name?: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const base = tone === 'teal' ? colors.teal : tone === 'red' ? '#e5484d' : colors.card;
  const bg = disabled ? '#1a2633' : hover ? (tone === 'card' ? colors.hover : tone === 'teal' ? '#2ee6d6' : '#f0676b') : base;
  const fg = disabled ? colors.muted : tone === 'teal' ? '#04211e' : colors.text;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.014} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={fontSize} maxWidth={w - .016} textAlign="center" color={fg}>{label}</Text>
  </group>;
}

/** "Talk to Zora", floating half a metre in front of the NPC, just below your eyes. */
function TalkPrompt({ npcId, name, onPress }: { npcId: string; name: string; onPress: () => void }) {
  const ref = useRef<THREE.Group>(null);
  const { camera } = useThree();
  useFrame(() => {
    const g = ref.current, body = npcBodies.get(npcId);
    if (!g?.parent || !body) return;
    camera.getWorldPosition(_head);
    body.getWorldPosition(_npc);
    _p.set(_head.x - _npc.x, 0, _head.z - _npc.z);
    const d = _p.length();
    if (d > 1e-3) _p.multiplyScalar(Math.min(0.5, d * 0.5) / d);
    _p.add(_npc); _p.y = _head.y - 0.3;
    g.position.copy(g.parent.worldToLocal(_p));
    g.lookAt(_head);
  });
  return <group ref={ref}>
    <Surface width={.348} height={.098} color={colors.border} z={-.003} r={.022} />
    <Btn name="vr-npc-talk" label={`Talk to ${clip(name, 18)}`} x={0} y={0} w={.34} h={.09} fontSize={.024} tone="teal" onPress={onPress} />
  </group>;
}

const W = .72;
const H = .36;

function ChatPanel({ worldId, npcId, name, hasGreeting, onClose }: { worldId: string; npcId: string; name: string; hasGreeting: boolean; onClose: () => void }) {
  const { lines, busy, resting, send, startRec, stopRec, recording } = useNpcConversation(worldId, npcId, hasGreeting);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');

  const lastNpc = [...lines].reverse().find((l) => l.role === 'assistant');
  const lastMe = [...lines].reverse().find((l) => l.role === 'user');
  const last: Line | undefined = lines[lines.length - 1];
  const note = last?.role === 'note' ? last.content : null;

  const status = resting ? `${name} is resting`
    : busy === 'thinking' ? `${name} is thinking…`
    : busy === 'listening' ? 'Listening…'
    : recording ? 'Speak now, then tap Send'
    : 'Tap the mic and talk';

  const onKey = (a: KeyAction) => {
    if (a.type === 'text') setDraft((d) => (d + a.value).slice(0, 400));
    else if (a.type === 'backspace') setDraft((d) => d.slice(0, -1));
    else if (draft.trim() && !busy && !resting) { void send(draft); setDraft(''); setTyping(false); }
  };

  // Typing grows the panel downwards and lifts it, so the keys aren't at your knees.
  const extra = typing ? .09 + KEYBOARD_HEIGHT + .03 : 0;
  const top = H / 2;
  return <group position={[0, typing ? .25 : 0, 0]} onClick={(e) => e.stopPropagation()}>
    <group position={[0, -extra / 2, 0]}>
      <Surface width={W + .008} height={H + extra + .008} color={colors.border} z={-.003} r={.022} />
      <Surface width={W} height={H + extra} color={colors.panel} r={.02} />
    </group>

    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .032, .006]} anchorX="left" fontSize={.026} color={colors.text}>{clip(name, 24)}</Text>
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .064, .006]} anchorX="left" fontSize={.0145} color={recording ? '#ff9a9d' : colors.muted}>{status}</Text>
    <Btn name="vr-npc-close" label="Close" x={W / 2 - .07} y={top - .045} w={.11} h={.05} fontSize={.016} onPress={onClose} />

    {lastMe && <Text raycast={noRaycast} position={[-W / 2 + .025, top - .1, .006]} anchorX="left" anchorY="top" fontSize={.015}
      color={colors.teal} maxWidth={W - .05}>{`You: ${clip(lastMe.content, 90)}`}</Text>}
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - (lastMe ? .13 : .1), .006]} anchorX="left" anchorY="top" fontSize={.021}
      lineHeight={1.25} color={lastNpc ? colors.text : colors.muted} maxWidth={W - .05}>
      {lastNpc ? clip(lastNpc.content, 230) : busy ? '…' : `Say hi to ${name}.`}
    </Text>
    {note && <Text raycast={noRaycast} position={[0, -top + .125, .006]} fontSize={.0145} color="#ffb1ad" maxWidth={W - .05} textAlign="center">{clip(note, 90)}</Text>}

    <Btn name="vr-npc-mic" x={-W / 2 + .025 + .23} y={-top + .055} w={.46} h={.075} fontSize={.021}
      tone={recording ? 'red' : 'teal'} disabled={resting || (!!busy && !recording)}
      label={recording ? 'Send what I said' : busy === 'listening' ? 'Listening…' : busy === 'thinking' ? 'Thinking…' : 'Tap to talk'}
      onPress={() => void (recording ? stopRec() : startRec())} />
    <Btn name="vr-npc-type" x={W / 2 - .025 - .105} y={-top + .055} w={.21} h={.075} fontSize={.019}
      label={typing ? 'Hide keys' : 'Type'} disabled={resting} onPress={() => setTyping((t) => !t)} />

    {typing && <group position={[0, -top - .02, 0]}>
      <group position={[0, -.035, 0]}>
        <Surface width={W - .05} height={.06} color={colors.card} z={.004} r={.012} />
        <Text raycast={noRaycast} position={[-W / 2 + .04, 0, .01]} anchorX="left" fontSize={.019} maxWidth={W - .08}
          color={draft ? colors.text : colors.muted}>{draft ? `${clip(draft.slice(-60), 60)}|` : `Say something to ${name}…`}</Text>
      </group>
      <group position={[0, -.09 - .031, .006]}>
        <VRKeyboard mode="text" goLabel="Send" onKey={onKey} />
      </group>
    </group>}
  </group>;
}

/** Inside the Canvas; renders only in VR. */
export function NpcTalkVR({ worldId, primitives }: { worldId: string; primitives: Record<string, PrimitiveItem> }) {
  const presenting = useXR((s) => !!s.session);
  const near = useNpcTalk((s) => s.near);
  const talkingTo = useNpcTalk((s) => s.talkingTo);
  const setTalkingTo = useNpcTalk((s) => s.setTalkingTo);
  const canTalk = useNpcCanTalk(presenting ? worldId : undefined, near);
  if (!presenting) return null;

  if (talkingTo) {
    const npc = primitives[talkingTo]?.npc;
    if (!npc?.talk?.enabled) return null;
    return <FloatingPanelMount distance={0.6} drop={0.4}>
      <ChatPanel key={talkingTo} worldId={worldId} npcId={talkingTo} name={npc.name} hasGreeting={!!npc.talk.greeting} onClose={() => setTalkingTo(null)} />
    </FloatingPanelMount>;
  }
  const npc = near ? primitives[near]?.npc : undefined;
  if (!near || !npc?.talk?.enabled || !canTalk) return null;
  return <TalkPrompt npcId={near} name={npc.name} onPress={() => setTalkingTo(near)} />;
}

export default NpcTalkVR;
